import * as React from 'react';
import { useLingui } from '@lingui/react/macro';
import { IShowroomConnectedInfo, IShowroomErrorEvent, IShowroomProps, Showroom } from 'components/Showroom';
import Header from 'components/Header';
import Footer from 'components/Footer';
import { isValidSessionID } from 'utils/session';
import { claimStreamHost } from 'state/session/streamHostClaim';
import {
  endSession,
  isSessionOrganizer,
  localAveTabId,
  reconcileVoipMasterClaim,
  releaseVoipMaster,
  sharingSnapshot,
  streamCanStart,
  userSession,
} from 'state/session';
import { startSessionForImages } from 'state/session/carImages';
import { showConfirmDialog } from 'state/confirm-dialog';
import { classNames } from 'utils/ui/css';
import { fireEvent, IntegrationType, integrationType } from 'utils/ui/app';
import { useWebComponent } from 'components/WebComponentRoot';
import { tenantConfig } from 'utils/tenant';
import { myCameraStream } from 'state/video';
import { initWebRtc, voipRtcConnection } from 'utils/ui/rtc';
import { isDeckFailureLocal, localDeckRetryDelay } from 'utils/ui/deckFailure';
import { user } from 'utils/auth/user';
import { analyticsState } from 'state/analytics';
import logger from 'utils/log';
import { BandwidthOptimisationConfig } from 'hooks/useBandwidthOptimisation';
import { ExperienceSwitcher } from 'containers/ExperienceSwitcher';
import Loader from 'components/Loader';
import useUnloadEvents from './useUnloadEvents';
import VoipPanel from './Voip/VoipPanel';
import Gallery from 'components/Micro2DApp/Gallery';
import Compare from 'components/Micro2DApp/Compare';
import { getComparisonVehicle } from 'state/session/compare';
import { LoadingScreen } from './LoadingScreen';
import { Stream3DErrorScreen } from 'components/Stream3DErrorScreen';
import { updateVehicle } from 'state/session/vehicleRegistry';
import { receiveShowroomPrString, runShowroomAnnouncement } from 'state/session/showroomAnnouncement';
import {
  getDeckPrStringHold,
  heldDeckModelRevision,
  heldDeckPrString,
  subscribeDeckPrString,
} from 'state/session/deckPrStringHold';
import PhotoSourceToggle, { PhotoSource } from 'components/PhotoSourceToggle/PhotoSourceToggle';
import { vtpPhotosToCarImageElements } from 'lambda/vehicle-data/vtp/vtp-parser';
import {
  hasPhotos,
  shouldShowPhotoToggle,
  getAlternatePreviewImage,
  selectGalleryAssets,
} from 'utils/photos/photoSelection';
import { jsonSafeParse } from 'utils/objects';
import { tvmCredentialsProvider } from 'utils/auth/tvm';
import { EnergyLabelOverlay } from 'components/EnergyLabel/EnergyLabel';
import { useEnergyLabelUrl } from 'components/EnergyLabel/useEnergyLabelUrl';
import { energyLabelState } from 'state/energyLabel';
import { launchedConfigurator } from 'utils/ui/webconfigurator';
import { EErrorCode } from 'config/errors-codes';
import { capacityRetryDelayMs, isCapacityCutoff, isJoinerCapacityError } from 'utils/ui/capacityRetry';
import {
  capacityFallbackCarKey,
  capacityFallbackGallery,
  closeCapacityCutoffPrompt,
  fetchCapacityFallbackImages,
  type ICapacityFallbackImages,
  promptCapacityCutoff,
  setCustomer3DUnavailable,
} from 'state/session/capacityCutoff';

export interface IStreamViewProps {
  className?: string;
  userType?: IShowroomProps['userType'];
  /**
   * Indicates whether the component is currently expanded or collapsed.
   * This is a controlled prop that reflects the current state of expansion.
   */
  expanded?: boolean;
  useVoip?: boolean;
  /**
   * Indicates if this instance of the StreamView is the session host.
   * This is used to determine if it is able to start new 3D sessions or load 2D content.
   * This handles cases when the active experience has not been fully loaded by the host yet.
   */
  isSessionHost?: boolean;

  onError?: () => void;
  bandwidthOptimisation?: BandwidthOptimisationConfig & {
    enabled?: boolean;
  };
}

/**
 * AVS-4460: a joiner's local wait for streaming capacity. `attempt === null` is idle; a number with
 * `retryDue === false` is waiting (deck unmounted); `retryDue === true` remounts the deck once.
 * `declined` is the joiner's "Switch to 2D" at the cutoff: the retries stop until the next reset.
 */
type TCapacityRetry = { attempt: number | null; retryDue: boolean; declined: boolean };

const CAPACITY_RETRY_IDLE: TCapacityRetry = { attempt: null, retryDue: false, declined: false };

/** Only a joiner's capacity failure is quiet — the host keeps its toast and its 2D fallback. */
const JOINER_QUIET_ERROR_CODES = [EErrorCode.NO_INSTANCES_AVAILABLE];

const StreamView: React.FC<IStreamViewProps> = ({
  userType = user.type,
  onError,
  className,
  useVoip = false,
  expanded = false,
  isSessionHost = true,
  bandwidthOptimisation = { enabled: false },
}) => {
  const { t } = useLingui();
  const session = userSession().useState();
  const deckHold = React.useSyncExternalStore(subscribeDeckPrString, getDeckPrStringHold);
  const elementRef = React.useRef<HTMLDivElement>(null);
  const { hasSlot, webComponent } = useWebComponent();
  tenantConfig().useState();
  const cameraStream = myCameraStream().useState();
  const [voipStream, setVoipStream] = React.useState<boolean>(false);
  const [voipActiveSpeaker, setVoipActiveSpeaker] = React.useState<boolean>(false);
  const [experience3DFailed, setExperience3DFailed] = React.useState<boolean>(false);
  // AVS-4482: this tab's own deck failed while another tab of the dealer carries the session; it re-mounts
  // when the tab is visible again, the stream changes, or the tab becomes the host.
  const [localDeckFailed, setLocalDeckFailed] = React.useState<boolean>(false);
  // Timer re-mounts since the deck last connected; caps the backoff in a tab the dealer is looking at.
  const localDeckRetriesRef = React.useRef(0);
  // AVS-4389: mirrors Showroom's switchInFlight window for the dealer, via onLocalAveSwitching.
  const [isLocalAveSwitching, setIsLocalAveSwitching] = React.useState<boolean>(false);
  const [photoSource, setPhotoSource] = React.useState<PhotoSource>('render');
  const [capacityRetry, setCapacityRetryState] = React.useState<TCapacityRetry>(CAPACITY_RETRY_IDLE);
  // Mirrors `capacityRetry` synchronously: a trailing deck event can reach `onAveError` before the
  // re-render that would give it a fresh closure (Showroom refreshes its onError ref in an effect).
  const capacityRetryRef = React.useRef(capacityRetry);
  const setCapacityRetry = React.useCallback((next: TCapacityRetry) => {
    const current = capacityRetryRef.current;
    if (next.attempt === current.attempt && next.retryDue === current.retryDue && next.declined === current.declined) {
      return;
    }
    capacityRetryRef.current = next;
    setCapacityRetryState(next);
  }, []);
  const isWaitingForCapacity = capacityRetry.attempt !== null && !capacityRetry.retryDue;
  // AVS-4460 part 2: the cutoff prompt's bookkeeping. The cycle token changes on every reset, so an
  // answer from a prompt opened in an earlier wait cycle is ignored. `pending` asks again on the next
  // failure when another dialog was busy; `open` marks a prompt this view opened.
  const capacityCutoffRef = React.useRef({ cycle: 0, pending: false, open: false });
  // AVS-4460: images the joiner's 2D fallback fetched itself, for when the shared gallery has none.
  const [capacityImages, setCapacityImages] = React.useState<ICapacityFallbackImages>();
  const gallery = capacityFallbackGallery(session, isWaitingForCapacity, capacityImages);
  const carKey = capacityFallbackCarKey(session);

  // Starts the fetch and never cancels it: the wait flips off on every retry of the deck, and an
  // answer landing in between still counts. Only an answer for a car no longer shown is dropped.
  React.useEffect(() => {
    if (!gallery.usesLocalImages || capacityImages?.carKey === carKey) return;
    void fetchCapacityFallbackImages().then((images) => {
      if (images.carKey === capacityFallbackCarKey(userSession().get())) setCapacityImages(images);
    });
  }, [gallery.usesLocalImages, carKey, capacityImages?.carKey]);

  // `useVoipConnections` must mount exactly once inside `MultiVoipInternal` (it drives
  // `voipRtcConnection.publish()`), so the active-speaker signal is bubbled up via the
  // `onActiveSpeakerChange` callback below.
  useUnloadEvents(userType);

  const isBroadcastSession = session.sharingType === 'broadcast' || session.sharingType === 'broadcast-local';
  const isWebComponent = integrationType() === IntegrationType.WebComponent;

  // Get the current vehicle for VTP photos
  const currentVehicle = userSession().currentVehicle;
  const vtpPhotos = currentVehicle?.vtpPhotos;
  const hasVtpPhotos = hasPhotos(vtpPhotos);
  const hasCarImages = hasPhotos(gallery.items);

  // AVS-4270: a blocked (outdated) car with no images from ANY source gets a description
  // panel instead of an empty gallery — for BOTH roles, since such a car will never get images
  const showNoImagesPanel = userSession().is3DBlocked && !hasCarImages && !hasVtpPhotos;

  // Convert VTP photos to car image elements for gallery display
  const vtpImageElements = React.useMemo(() => {
    if (!hasVtpPhotos || !vtpPhotos) return [];
    return vtpPhotosToCarImageElements(vtpPhotos);
  }, [hasVtpPhotos, vtpPhotos]);

  // Toggle is only visible when BOTH carImages AND vtpPhotos are available, and NOT in comparison mode 'on'
  const showPhotoToggle = shouldShowPhotoToggle(hasCarImages, hasVtpPhotos, user.isDealer, session.comparisonMode);

  // Get the first image from the alternate source for the toggle preview
  const alternatePreviewImage = React.useMemo(() => {
    return getAlternatePreviewImage(photoSource, vtpImageElements, gallery.items);
  }, [photoSource, vtpImageElements, gallery.items]);

  // Select assets based on photo source and availability
  const galleryAssets = React.useMemo(() => {
    return selectGalleryAssets(photoSource, hasCarImages, hasVtpPhotos, vtpImageElements, gallery.items);
  }, [photoSource, hasCarImages, hasVtpPhotos, vtpImageElements, gallery.items]);

  // Reset photo source when vehicle changes
  React.useEffect(() => {
    setPhotoSource('render');
  }, [currentVehicle?.prString]);

  // Get the comparing vehicle (if any)
  const [comparingVehicleIdFromRegistry, comparingVehicle] = getComparisonVehicle();

  // For sessions in 3D mode, non-host participants (customers and second dealers) must wait for aveSessionId/aveChannelId
  // This prevents loading the session before the host dealer's stream is initialized
  const waitingForStream =
    !isSessionHost && session.activeExperience === '3D' && session.streamActive && !userSession().hasStreamingId;

  React.useEffect(() => {
    if (session.carStatus === 'ready' && session.activeExperience === '2D' && !session.id && user.type === 'dealer') {
      // AVS-4353: the session start and the optimistic 'loading' write are one transition —
      // splitting them left the gallery stranded whenever registration produced no id.
      void startSessionForImages();
    }
    setExperience3DFailed(false);
  }, [session.id, session.carStatus, session.activeExperience]);

  // AVS-4461: both are deps of the VOIP effect below. A remount can read `voip: false` while
  // tenant config reloads and tear VOIP down; the config landing must re-run the effect.
  const voipAllowed = tenantConfig().isAllowed('voip');
  const singleStreamEnabled = userSession().isVoipSingleStreamEnabled;

  React.useEffect(() => {
    if (
      session.streamActive &&
      session.customerAllowed && // this flag is set by the dealer when waiting room is closed
      session.sharingType === 'remote' &&
      voipAllowed &&
      // A completed presentation must stay torn down — `initWebRtc`'s same-session
      // early-return sits before its own `presentationCompleted` check, so it would
      // otherwise fire the callback here.
      !userSession().presentationCompleted
    ) {
      // Deliberately unconditional: `voipStream` is local state fed only by this callback, so
      // a remount (DCC re-creates `<e3d-stream>` on "Neue Konfiguration") must re-fire it even
      // with a live connection. `initWebRtc` is idempotent — a same-session call early-returns
      // and fires the callback synchronously (pinned in `utils/ui/rtc-reentrant.test.ts`).
      initWebRtc(session.id, () => {
        setVoipStream(true);
      });
    } else if (voipRtcConnection) {
      // AVS-4302 (CG-3): under the single-stream election, a receive-only standby's
      // connection must survive benign predicate flips (streamActive / customerAllowed /
      // sharingType churn) — only a GENUINE session end (`presentationCompleted`) tears
      // it down. Flag-off ⇒ `isVoipSingleStreamEnabled` is false ⇒ destroy on any
      // predicate-false flip exactly as today (and a real end still destroys).
      if (!singleStreamEnabled || userSession().presentationCompleted) {
        voipRtcConnection.destroy();
        setVoipStream(false);
        // AVS-4461: an involuntary teardown frees this tab's master slot so a standby can take
        // over. A no-op unless this tab is the master of a live, not completed session.
        releaseVoipMaster();
      }
    }
    return () => {
      // AVS-4302 (CG-3): the cleanup runs on EVERY dep change, so an unconditional
      // `setVoipStream(false)` would unmount the VoipPanel for a receive-only standby on
      // a benign flip — even though the connection itself was correctly kept alive above.
      // Gate the panel reset the same way as the teardown: only hide on flag-off or a
      // genuine session end. Flag-off ⇒ today's behaviour (always reset on cleanup).
      if (!userSession().isVoipSingleStreamEnabled || userSession().presentationCompleted) {
        setVoipStream(false);
      }
    };
  }, [
    session.customerAllowed,
    session.id,
    session.sharingType,
    session.streamActive,
    voipAllowed,
    singleStreamEnabled,
  ]);

  // AVS-4302 (Task 8, AC b — "one stream on the wire"): master-claim reconciliation +
  // old-master step-down. The DECISION lives in state (`reconcileVoipMasterClaim`, backed by
  // the per-tab claim memory in voipClaim.ts — set synchronously at every claim site, so a
  // crossed claim landing before a render commit is still attributed correctly); this effect
  // is a thin trigger owning only the RTC side effect. Outcomes: a genuine takeover (or a
  // lost tie-break in the simultaneous-claim race) steps this tab down to receive-only; a
  // stale/crossed write this tab outranks is re-asserted inside the reconciler; a vacant
  // slot is ignored (C1: transient clobber self-heals via the gate re-claim) — except by a
  // standby with a working media-less connection, which claims it pending "Take over" (AVS-4461).
  const stepDownSessionIdRef = React.useRef(session.id);
  const mySlot = session.voipMasters?.[user.userPseudonym];
  // Composite dep: a broadcast can change `gen` WITHOUT changing tabId (stale echo, clobber
  // heal) and the reconciler must see those ticks — a tabId-only dep would skip them.
  const masterSlotKey = mySlot ? `${mySlot.tabId}:${mySlot.gen ?? 0}` : '';
  React.useEffect(() => {
    // A new session carries no claim history (state layer clears it on load/start/end) —
    // skip the reconcile on the id-transition tick itself.
    if (stepDownSessionIdRef.current !== session.id) {
      stepDownSessionIdRef.current = session.id;
      return;
    }

    if (reconcileVoipMasterClaim() && voipRtcConnection) {
      // AVS-4302 telemetry: a handoff occurred and THIS tab stepped down — lets support
      // reconstruct "dealer froze / saw twice" reports against the elected master timeline.
      logger.log('[VOIP] AVS-4302 step-down: lost mastership, reconnecting receive-only', {
        sessionId: session.id,
        newMasterTabId: userSession().get('voipMasters')?.[user.userPseudonym]?.tabId,
      });
      // Reconnect receive-only: with isVoipStandby now true, initWebRtc skips the camera
      // grab and the publish gate stays closed (Task 9 makes this a clean rebuild). Pass the
      // same voipStream callback as the main effect so the panel stays visible post-step-down.
      voipRtcConnection.destroy();
      void initWebRtc(session.id, () => setVoipStream(true));
    }
  }, [masterSlotKey, session.id]);

  React.useEffect(() => {
    if ((cameraStream.isAudioEnabled || cameraStream.isVideoEnabled) && !analyticsState().get('voipUsed')) {
      analyticsState().setValues({ voipUsed: true });
    }
  }, [cameraStream.isAudioEnabled, cameraStream.isVideoEnabled]);

  // AVS-4460: retry the joiner's deck on a backoff. The cleanup clears the timer on unmount, on an
  // attempt change, and on a new stream id (it runs before the reset effect below).
  // A declined wait schedules nothing. An open cutoff prompt does not pause the timer.
  React.useEffect(() => {
    if (capacityRetry.attempt === null || capacityRetry.retryDue || capacityRetry.declined) return;
    const { attempt } = capacityRetry;
    const timer = setTimeout(
      () => setCapacityRetry({ attempt, retryDue: true, declined: false }),
      capacityRetryDelayMs(attempt)
    );
    return () => clearTimeout(timer);
  }, [capacityRetry, session.aveSessionId, setCapacityRetry]);

  /**
   * Ends the wait cycle: a later answer from its prompt is ignored, and the prompt closes.
   * Only `deckConnected` clears the customer flag. A new stream id keeps it: 3D is still missing.
   */
  const resetCapacityWait = React.useCallback(
    (deckConnected: boolean) => {
      const cutoff = capacityCutoffRef.current;
      cutoff.cycle += 1;
      cutoff.pending = false;
      if (cutoff.open) {
        cutoff.open = false;
        closeCapacityCutoffPrompt();
      }
      if (deckConnected) {
        setCustomer3DUnavailable(false);
      }
      setCapacityRetry(CAPACITY_RETRY_IDLE);
    },
    [setCapacityRetry]
  );

  // A new stream from the dealer is a fresh start — mount the deck against it straight away, and a
  // declined joiner is asked again only if the new stream also reaches the cutoff.
  React.useEffect(() => {
    resetCapacityWait(false);
    setLocalDeckFailed(false);
  }, [session.aveSessionId, resetCapacityWait]);

  // AVS-4482: a standby that takes over becomes host on the same stream; its joiner wait and failure are over.
  const wasSessionHostRef = React.useRef(isSessionHost);
  React.useEffect(() => {
    if (isSessionHost && !wasSessionHostRef.current) {
      resetCapacityWait(false);
      setLocalDeckFailed(false);
    }
    wasSessionHostRef.current = isSessionHost;
  }, [isSessionHost, resetCapacityWait]);

  // AVS-4482: give a locally failed deck another try once the dealer looks at this tab again. A tab the
  // dealer is already looking at gets no visibility event, so it retries on a short, capped backoff.
  React.useEffect(() => {
    if (!localDeckFailed) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') setLocalDeckFailed(false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    const delay = localDeckRetryDelay(localDeckRetriesRef.current);
    const retryTimer =
      delay === undefined
        ? undefined
        : setTimeout(() => {
            if (document.visibilityState !== 'visible') return;
            localDeckRetriesRef.current += 1;
            setLocalDeckFailed(false);
          }, delay);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearTimeout(retryTimer);
    };
  }, [localDeckFailed]);

  // The flag stays on unmount: the joiner still has no 3D until a deck connects.
  React.useEffect(
    () => () => {
      if (capacityCutoffRef.current.open) {
        capacityCutoffRef.current.open = false;
        closeCapacityCutoffPrompt();
      }
    },
    []
  );

  // Never awaited in a chain: a closed prompt never settles (see `promptCapacityCutoff`).
  const askCapacityCutoff = () => {
    const cutoff = capacityCutoffRef.current;
    const { cycle } = cutoff;
    cutoff.pending = false;
    cutoff.open = true;
    void promptCapacityCutoff().then((answer) => {
      if (cycle !== cutoff.cycle) return;
      cutoff.open = false;
      if (answer === 'busy') {
        cutoff.pending = true;
      } else if (answer === 'switch') {
        const { attempt } = capacityRetryRef.current;
        if (attempt !== null) setCapacityRetry({ attempt, retryDue: false, declined: true });
      }
    });
  };

  const onAveError = (error: IShowroomErrorEvent, errorData: Record<string, unknown> = {}) => {
    const retry = capacityRetryRef.current;

    // A trailing event (e.g. `disconnected`) from the deck that already failed on capacity.
    if (retry.attempt !== null && !retry.retryDue) return undefined;

    if (isJoinerCapacityError(errorData, isSessionHost)) {
      // AVS-4460: a LOCAL condition. Never write `activeExperience` (shared — the dealer's next
      // broadcast would put it back to 3D and loop) and never call `onError` (the wait is not terminal).
      const attempt = (retry.attempt ?? -1) + 1;
      setCapacityRetry({ attempt, retryDue: false, declined: false });
      logger.warn(`[AVS-4460] no streaming capacity for joiner, retry #${attempt + 1} scheduled`);
      if (attempt === 0) {
        fireEvent({ el: elementRef.current, name: 'e3d-stream-error', detail: { userType, session, error } });
      }
      if (isCapacityCutoff(attempt)) {
        // Without 2D there is nothing to switch to: flag the dealer, keep retrying, ask nothing.
        // Only a customer's flag reaches the dealer; a second dealer gets the local prompt only.
        const canSwitch = tenantConfig().isAllowed('2D');
        logger.warn(`[AVS-4460] capacity cutoff reached after ${attempt + 1} failed attempts`, { prompt: canSwitch });
        capacityCutoffRef.current.pending = canSwitch;
        setCustomer3DUnavailable(true);
      }
      if (capacityCutoffRef.current.pending) askCapacityCutoff();
      // `undefined`, not `false`, is the quiet path in Showroom's handleStreamError: `false` throws
      // INTERNAL_ERROR in web component mode.
      return undefined;
    }

    if (isDeckFailureLocal()) {
      logger.warn('[AVS-4482] standby deck failed; keeping the session in 3D', {
        type: error.type,
        message: error.message,
      });
      setLocalDeckFailed(true);
      return undefined;
    }

    onError?.();

    if (tenantConfig().isAllowed('2D')) {
      userSession().setValues({
        activeExperience: '2D',
      });
    } else {
      // If 3D fails and 2D is not allowed, we show a fallback screen
      setExperience3DFailed(true);
    }

    const canContinue = fireEvent({
      el: elementRef.current,
      name: 'e3d-stream-error',
      detail: { userType, session, error },
    });
    if (!canContinue) return;

    console.error('Error connecting to stream', error);
    return false;
  };

  const aveSessionIdForDeck = isValidSessionID(session.aveSessionId) ? session.aveSessionId : undefined;

  const onStreamConnected = ({ publishedStreamId }: IShowroomConnectedInfo) => {
    resetCapacityWait(true);
    localDeckRetriesRef.current = 0;
    const canContinue = fireEvent({ el: elementRef.current, name: 'e3d-stream-start', detail: { userType, session } });
    if (!canContinue) {
      userSession().setValues({ streamActive: false });
      return;
    }
    // Only the context whose own deck published the id is the stream host. The deck reports that
    // fact directly, so this no longer re-derives it from a render closure. A second dealer whose
    // deck attached to an id someone else published must not claim, or it would take the host role
    // away from the real organizer (AVS-4452).
    if (isSessionHost && publishedStreamId) claimStreamHost(session.id);
  };

  const onLocalAveOverride = () => {
    // AVS-4386: claim the dialog in the same shadow root that triggered it (e3d-stream's fullscreen overlay)
    showConfirmDialog({
      dialogId: 'DIALOG_LOCAL_AVE_OVERRIDE',
      title: t`Local AVE disconnected`,
      message: t`Another user has taken over the local rendering device. Your session will continue in 2D mode.`,
      confirmText: t`OK`,
      hideCancel: true,
      preferredClaimant: webComponent?.tagName?.toLowerCase(),
    });

    // Local AVE params are cleared unconditionally and BEFORE branching — endSession()
    // early-returns with zero cleanup for customers and non-organizer dealers (AVS-4362 review).
    userSession().setValues({
      aveChannelName: '',
      renderer: 'cloud',
      aveClientId: '',
      localAveTakenOver: true,
    });

    if (!tenantConfig().isAllowed('2D')) {
      void endSession({
        reason: 'local-ave-takeover-no-2d-fallback',
        leaveWithoutEnding: !isSessionOrganizer(),
      }).catch((error) => console.error('Failed to end session after local AVE takeover:', error));
      return;
    }

    // Step 5b: preserve streamingDevice so isLocalAveDeviceLost stays derivable for downstream ACs
    userSession().setValues({ activeExperience: '2D' });
  };

  const isLocalAveSession = userSession().isLocalAveSession;

  // When another tab has claimed the local AVE connection, this tab should not attempt to connect
  const isLocalAveClaimedByAnotherTab =
    isLocalAveSession && !!session.aveClientId && session.aveClientId !== localAveTabId;

  // AVS-4389: onLocalAveSwitching is only passed to Showroom while isLocalAveSession is true, so
  // a mid-switch endSession() drops the prop entirely instead of calling it with false — force-clear here.
  React.useEffect(() => {
    if (!isLocalAveSession) {
      setIsLocalAveSwitching(false);
    }
  }, [isLocalAveSession]);

  const energyLabelUrl = useEnergyLabelUrl(session.activeVehicleId);
  const comparingVehicleId = session.comparisonMode === 'on' ? comparingVehicleIdFromRegistry : undefined;
  const comparingLabelUrl = useEnergyLabelUrl(comparingVehicleId);

  // Track how many energy label overlays are currently maximized (for VOIP shift)
  energyLabelState().useState();
  const energyMaximizedCount = React.useMemo(() => {
    if (session.comparisonMode !== 'on') {
      return energyLabelUrl && energyLabelState().getSize(session.activeVehicleId ?? '') === 'maximized' ? 1 : 0;
    }
    let count = 0;
    if (energyLabelUrl && energyLabelState().getSize(session.activeVehicleId ?? '') === 'maximized') count++;
    if (comparingLabelUrl && comparingVehicleId && energyLabelState().getSize(comparingVehicleId) === 'maximized')
      count++;
    return count;
  }, [
    session.comparisonMode,
    session.activeVehicleId,
    energyLabelUrl,
    comparingLabelUrl,
    comparingVehicleId,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    energyLabelState().get('labelSizes'),
  ]);

  if (!userSession().sessionReadyForExperience) {
    return (
      <div
        className={classNames(
          'relative w-full h-full flex flex-col',
          className,
          isBroadcastSession && 'e3d-sixteen-nine'
        )}
        ref={elementRef}
      >
        <EnergyLabelOverlay vehicleId={session.activeVehicleId ?? ''} imageUrl={energyLabelUrl} />
      </div>
    );
  }

  const experience2D: React.JSX.Element = (
    <>
      {gallery.status === 'unset' && !user.isDealer && (
        <LoadingScreen
          title={t`Vehicle Image Coming Soon`}
          message={t`The image for this vehicle is currently being processed and will be available shortly. Thank you for your patience.`}
        />
      )}
      {gallery.status === 'unset' && user.isDealer && (
        <div className="relative z-0 size-full grow overflow-hidden bg-home-page bg-[length:100%_100%]" />
      )}
      {gallery.status === 'loading' && <Loader className="size-full" />}
      {/* AVS-4270: full-screen description for a blocked (outdated) car with no images (PO decision 3) */}
      {gallery.status === 'ready' && showNoImagesPanel && (
        <LoadingScreen
          title={t`Images are not available`}
          message={t`There are no images available for this vehicle.`}
        />
      )}
      {gallery.status === 'ready' &&
        !showNoImagesPanel &&
        (session.comparisonMode === 'on' &&
        !!comparingVehicle?.carImages?.items.length &&
        session.carImages?.items?.length ? (
          <Compare
            car1={session.carImages?.items ?? []}
            car2={comparingVehicle.carImages?.items ?? []}
            brand={session.brand}
            template={session.brand}
            theme={session.brand}
            features={{ fullscreen: !isWebComponent && session.sharingType !== 'broadcast' }}
            customerAllowance={session.customerAllowance}
          />
        ) : (
          <div className="relative size-full">
            <Gallery
              assets={galleryAssets}
              brand={session.brand}
              template={session.brand}
              theme={session.brand}
              features={{ fullscreen: !isWebComponent && session.sharingType !== 'broadcast' }}
              customerAllowance={session.customerAllowance}
            />
            {showPhotoToggle && (
              <PhotoSourceToggle
                currentSource={photoSource}
                previewImageUrl={alternatePreviewImage}
                onToggle={() => setPhotoSource((prev) => (prev === 'render' ? 'real' : 'render'))}
              />
            )}
          </div>
        ))}
    </>
  );

  const allows2D = tenantConfig().isAllowed('2D');
  const capacityFallback: React.JSX.Element = allows2D ? (
    experience2D
  ) : (
    <LoadingScreen title={t`3D view unavailable`} message={t`Retrying when streaming capacity is free…`} />
  );

  const experience3D: React.JSX.Element = (
    <>
      {/* Show loading screen while waiting for dealer's stream to initialize in device sessions */}
      {waitingForStream && (
        <LoadingScreen
          title={t`Connecting to Stream`}
          message={t`Waiting for the dealer to start the 3D experience...`}
        />
      )}
      {/* Show info screen when another tab is controlling the local AVE device (local AVEs do not support multistreams) */}
      {!waitingForStream && isLocalAveClaimedByAnotherTab && (
        <div className="absolute inset-0">
          <LoadingScreen
            title={t`Local renderer in use`}
            message={t`Local renderers only support a single tab at a time and the stream is currently active in another tab.`}
          />
        </div>
      )}
      {!waitingForStream && !isLocalAveClaimedByAnotherTab && experience3DFailed && (
        <div className="absolute inset-0 z-10 size-full">
          <Stream3DErrorScreen onRetry={() => setExperience3DFailed(false)} />
        </div>
      )}
      {/* AVS-4389: covers the gap between tearing down device A and the new stream arriving from
          device B. Overlay, not exclusive with Showroom below — the deck must stay mounted. */}
      {!waitingForStream && !isLocalAveClaimedByAnotherTab && !experience3DFailed && isLocalAveSwitching && (
        <div className="absolute inset-0 z-10 size-full">
          <LoadingScreen title={t`Switching device`} message={t`Connecting to the selected local renderer…`} />
        </div>
      )}
      {!waitingForStream &&
        !isLocalAveClaimedByAnotherTab &&
        !experience3DFailed &&
        (!localDeckFailed && (isSessionHost || (session.streamActive && userSession().hasStreamingId)) ? (
          isWaitingForCapacity ? (
            capacityFallback
          ) : (
            <Showroom
              // AVS-4462: a car derived from a broadcast is held back from the deck, which would selectCar it.
              prString={heldDeckPrString(session.prString, deckHold)}
              modelRevision={heldDeckModelRevision(session, session.vehicles, deckHold)}
              market={session.market!}
              brand={session.brand!}
              active={streamCanStart()}
              data-testid="3D"
              suppressErrorToast={!allows2D}
              quietErrorCodes={isSessionHost ? undefined : JOINER_QUIET_ERROR_CODES}
              className={classNames(
                'grow bg-black',
                userType === 'customer' ? !session.customerAllowance && 'pointer-events-none' : '',
                expanded ? 'opacity-100' : 'absolute left-0 top-0 w-full grow object-cover'
              )}
              onConnected={onStreamConnected}
              onError={onAveError}
              sessionId={aveSessionIdForDeck}
              customerAllowance={session.customerAllowance}
              // [AVS-3617] Showroom's fullscreen should be disabled for the web component, but enabled for the standalone.
              // In standalone mode, the fullscreen button is only disabled in NERD broadcast sessions.
              features={{ fullscreen: !isWebComponent && session.sharingType !== 'broadcast' }}
              hideConsumptionData={true}
              onUpdateConsumptionData={(wltpText, deckPrString) => {
                // Bind the WLTP to the configuration the deck computed it for. Uncorrelated, a quick
                // A → B switch lands A's numbers on B — the race this guard exists for — and our own
                // call is indistinguishable from DCC's in a `?log=warn` capture (AVS-4400).
                // A customer applies it locally and never syncs it (AVS-4412).
                if (wltpText)
                  runShowroomAnnouncement(() =>
                    updateVehicle({ wltpText }, deckPrString ? { expect: { prString: deckPrString } } : undefined)
                  );
              }}
              // AVS-4422: one entry point in state; DCC and web components keep the identifiers DCC supplies (AVS-4399).
              onUpdatePrString={(prString, announcement) =>
                receiveShowroomPrString(prString, announcement, {
                  webComponent: launchedConfigurator === 'DCC' || isWebComponent,
                })
              }
              bandwidthOptimisation={bandwidthOptimisation}
              /** Passing the localAveName and the credentialsProvider will lead to a local AVE session, instead of cloud streaming
               * We want to make sure local AVEs are only used when the sharing type is set accordingly and the session is shared.
               */
              {...(isLocalAveSession
                ? {
                    credentialsProvider: tvmCredentialsProvider,
                    // PR review (AVS-4389): read the COMMITTED device (sharingSnapshot, set atomically
                    // with isShared by share()'s queue), not live session.streamingDevice — the dashboard
                    // radio writes that live, unauthorised, on every pick. Using it here meant an
                    // unconfirmed pick could reach the deck on the next mount (e.g. a 2D<->3D toggle,
                    // which fully unmounts/remounts Showroom) with no share() call, no confirm dialog,
                    // and no LOCAL_AVE_SWITCH_EVENT ever firing.
                    localAveName: jsonSafeParse<{ id?: string }>(sharingSnapshot().streamingDevice ?? '', {}).id,
                    onLocalAveOverride: onLocalAveOverride,
                    onLocalAveSwitching: setIsLocalAveSwitching,
                    localAveClientId: localAveTabId,
                  }
                : {})}
            />
          )
        ) : (
          experience2D
        ))}
    </>
  );

  const experiences = [
    {
      name: '3D' as const,
      element: experience3D,
    },
    {
      name: '2D' as const,
      element: experience2D,
    },
  ];

  return (
    <div
      className={classNames(
        'relative w-full h-full flex flex-col',
        className,
        isBroadcastSession && 'e3d-sixteen-nine'
      )}
      ref={elementRef}
      data-energy-maximized={energyMaximizedCount}
    >
      {expanded && <Header withControls={user.isDealer} />}
      {!hasSlot && (
        <ExperienceSwitcher
          experiences={experiences}
          activeExperience={session.activeExperience}
          fillContainer={isWaitingForCapacity}
        />
      )}
      {session.comparisonMode === 'on' ? (
        <div className="ex3d-energy-label-comparison-overlays absolute bottom-[5.5rem] left-4 z-[100] flex gap-2">
          <EnergyLabelOverlay vehicleId={session.activeVehicleId ?? ''} imageUrl={energyLabelUrl} />
          {comparingLabelUrl && (
            <EnergyLabelOverlay vehicleId={comparingVehicleId ?? ''} imageUrl={comparingLabelUrl} />
          )}
        </div>
      ) : (
        <EnergyLabelOverlay vehicleId={session.activeVehicleId ?? ''} imageUrl={energyLabelUrl} />
      )}
      {(expanded || useVoip) && voipStream && (
        <VoipPanel
          className="ex3d-stream--voip-container"
          controls
          voipStream={voipStream}
          isActiveSpeaker={voipActiveSpeaker}
          onActiveSpeakerChange={setVoipActiveSpeaker}
        />
      )}
      {expanded && <Footer userType={userType} />}
    </div>
  );
};

export default StreamView;
